import type { CompareSummary, CompareSummaryTarget } from '../services/dataforseo';

/**
 * Which rival the Compare button opens on, and whether to offer it at all.
 *
 * **This file used to compose a whole evidence row** — seven states of copy
 * for a "Compare" line in the Dashboard recap, reading things like *"Their
 * page is longer (3.9k vs your 2.4k words)"*. That row is gone. Word counts
 * and ranked-keyword totals are dive-in numbers: an operator reading "their
 * page is longer" has no next move, and rank was already answered one row
 * down by Competition. They belong in the drawer, which shows all of them
 * side by side, and the recap keeps only the click that gets there.
 *
 * What survived the deletion is the part that was never copy: knowing when
 * there is a rival worth opening. The `git show` before this commit has the
 * old wording if a Compare surface ever wants it back.
 */

/**
 * The compare target, or null when there is nothing to open.
 *
 * Null in the three states where no page is a candidate — you already hold #1,
 * nothing above you is comparable, or no SERP has been taken. It is
 * deliberately NOT null for the states where we hold a target we could not
 * read (`target_uncrawled`, `rival_crawl_failed`, `target_not_retrieved`):
 * the drawer opens on those and offers to fetch, and now that opening it costs
 * nothing, refusing the click would be hiding a working action to avoid
 * mentioning a failure.
 */
export function compareDrawerTarget(
  summary: CompareSummary | null | undefined
): CompareSummaryTarget | null {
  if (!summary) return null;
  switch (summary.state) {
    case 'you_are_top':
    case 'no_target':
    case 'no_serp':
      return null;
    default:
      return summary.target ?? null;
  }
}
